export type FacetType = 'entity' | 'entityGroup' | 'attribute' | 'property' | 'classification' | 'material' | 'partOf';

/** Per-value field: supports the 6 data types (Wert, Aufzählung, Grenzen, Muster, Länge, Länge Grenzen) */
export interface IDSValueField {
  dataType: DataType;
  simpleValue?: string;       // Wert
  enumValues: string[];        // Aufzählung
  rangeMin?: string;           // Grenzen
  rangeMax?: string;           // Grenzen
  pattern?: string;            // Muster
  lengthValue?: string;        // Länge
  lengthMin?: string;          // Länge Grenzen
  lengthMax?: string;          // Länge Grenzen
}

export function createDefaultValueField(): IDSValueField {
  return { dataType: 'Wert', simpleValue: '', enumValues: [] };
}

export interface IDSFacet {
  id: string;
  type: FacetType;
  expanded: boolean;

  // Entität
  entityName: IDSValueField;
  predefinedType: IDSValueField;
  /**
   * „Subklassen mitprüfen" (#2311): expandiert die angegebenen IFC-Klassen beim Serialisieren um
   * ihren Subtyp-Abschluss.
   *
   * <p>Nötig, weil `ifctester` Entitäten exakt vergleicht (`Entity.filter` mit
   * `include_subtypes=False`): eine Spezifikation auf `IfcWall` erfasst sonst keine
   * `IfcWallStandardCase`, obwohl Wände oft genau so exportiert werden.
   *
   * <p>Reines UI-Flag — im IDS-XML steht nur die expandierte Aufzählung, damit die Regel für
   * Fremdwerkzeuge lesbar bleibt. Beim Laden wird es aus der Aufzählung wiedererkannt
   * (`matchSubtypeClosure`); ein Feld im Modell braucht es trotzdem, weil der Editor die vom
   * Nutzer eingetippten Elternklassen zeigen soll und nicht deren Expansion.
   */
  includeSubtypes?: boolean;

  // Attribut
  attributeName: IDSValueField;
  attributeValue: IDSValueField;

  // Eigenschaft
  propertySet: IDSValueField;
  propertyName: IDSValueField;
  propertyDataType?: string;
  propertyValue: IDSValueField;

  // Klassifikation
  classificationSystem: IDSValueField;
  classificationValue: IDSValueField;

  // Material
  materialName: IDSValueField;

  // Teil von
  relation?: string;
  partOfEntityName: IDSValueField;
  partOfPredefinedType: IDSValueField;

  // Metainformationen (nur bei Anforderungen)
  metaDescription?: string;
  metaNecessity?: NecessityType;
  metaUri?: string;
  metaInstruction?: string;
}

export type NecessityType = 'required' | 'optional' | 'prohibited';

export const NECESSITY_OPTIONS: { value: NecessityType; label: string }[] = [
  { value: 'required', label: 'Erforderlich' },
  { value: 'optional', label: 'Optional' },
  { value: 'prohibited', label: 'Verboten' }
];

export const IFC_VERSIONS = ['IFC2X3', 'IFC4', 'IFC4X3'];

export interface IDSSpecification {
  id: string;
  name: string;
  description?: string;
  necessity: NecessityType;
  instruction?: string;
  ifcVersions: string[];
  identifier?: string;
  applicability: IDSFacet[];
  requirements: IDSFacet[];
}

export interface IDSInfo {
  title: string;
  copyright?: string;
  version?: string;
  description?: string;
  author?: string;
  date?: string;
  purpose?: string;
  milestone?: string;
}

export interface IDSRule {
  id?: string;
  info: IDSInfo;
  specifications: IDSSpecification[];
}

export const FACET_TYPE_LABELS: Record<FacetType, string> = {
  entity: 'Entität',
  entityGroup: 'Entitätengruppe',
  attribute: 'Attribut',
  property: 'Eigenschaft',
  classification: 'Klassifikation',
  material: 'Material',
  partOf: 'Teil von'
};

/** `applicabilityOnly`: Facettentyp ist nur in der Anwendbarkeit wählbar, nicht bei Anforderungen. */
export const FACET_TYPES: { value: FacetType; label: string; applicabilityOnly?: boolean }[] = [
  { value: 'entity', label: 'Entität' },
  { value: 'entityGroup', label: 'Entitätengruppe', applicabilityOnly: true },
  { value: 'attribute', label: 'Attribut' },
  { value: 'property', label: 'Eigenschaft' },
  { value: 'classification', label: 'Klassifikation' },
  { value: 'material', label: 'Material' },
  { value: 'partOf', label: 'Teil von' }
];

export type DataType = 'Wert' | 'Aufzählung' | 'Grenzen' | 'Muster' | 'Länge' | 'Länge Grenzen';

export const DATA_TYPES: DataType[] = ['Wert', 'Aufzählung', 'Grenzen', 'Muster', 'Länge', 'Länge Grenzen'];

export const PROPERTY_DATA_TYPES = [
  'IfcLabel',
  'IfcText',
  'IfcInteger',
  'IfcReal',
  'IfcBoolean',
  'IfcIdentifier'
];

/** Vom IDS-1.0-Schema für ids:partOf/@relation als xs:enumeration erlaubte Werte. */
export const RELATIONS = [
  'IfcRelAggregates',
  'IfcRelAssignsToGroup',
  'IfcRelContainedInSpatialStructure',
  'IfcRelNests',
  'IfcRelVoidsElement',
  'IfcRelFillsElement'
];

export function createDefaultFacet(type: FacetType): IDSFacet {
  return {
    id: crypto.randomUUID(),
    type,
    expanded: true,
    // Startet ohne vorausgewählte Gruppe.
    entityName: type === 'entityGroup'
      ? { ...createDefaultValueField(), dataType: 'Aufzählung' }
      : createDefaultValueField(),
    predefinedType: createDefaultValueField(),
    includeSubtypes: false,
    attributeName: createDefaultValueField(),
    attributeValue: createDefaultValueField(),
    propertySet: { ...createDefaultValueField(), simpleValue: '.*' },
    propertyName: createDefaultValueField(),
    propertyValue: createDefaultValueField(),
    classificationSystem: createDefaultValueField(),
    classificationValue: createDefaultValueField(),
    materialName: createDefaultValueField(),
    partOfEntityName: createDefaultValueField(),
    partOfPredefinedType: createDefaultValueField()
  };
}

export function createDefaultSpecification(): IDSSpecification {
  const reqFacet = createDefaultFacet('property');
  reqFacet.metaNecessity = 'required';
  return {
    id: crypto.randomUUID(),
    name: '',
    necessity: 'required',
    ifcVersions: [],
    applicability: [createDefaultFacet('entity')],
    requirements: [reqFacet]
  };
}

/** Returns true if the value field has no meaningful content (i.e. is "empty"). */
export function isValueFieldEmpty(field: IDSValueField): boolean {
  switch (field.dataType) {
    case 'Wert':
      return !field.simpleValue?.trim();
    case 'Aufzählung':
      return !field.enumValues || field.enumValues.length === 0;
    case 'Muster':
      return !field.pattern?.trim();
    case 'Grenzen':
      return !field.rangeMin?.trim() && !field.rangeMax?.trim();
    case 'Länge':
      return !field.lengthValue?.trim();
    case 'Länge Grenzen':
      return !field.lengthMin?.trim() && !field.lengthMax?.trim();
    default:
      return !field.simpleValue?.trim();
  }
}

/**
 * Returns true if the given value is a valid ISO date (xs:date, YYYY-MM-DD).
 * The string must match the ISO format exactly and represent a real calendar date,
 * otherwise the date is considered invalid.
 */
export function isValidIsoDate(value: string | null | undefined): boolean {
  if (!value) return false;
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return false;
  const date = new Date(`${trimmed}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return false;
  return date.toISOString().slice(0, 10) === trimmed;
}

/**
 * Das IDS-1.0-Schema schränkt <ids:author> auf eine E-Mail-Adresse ein
 * (`xs:pattern value="[^@]+@[^\.]+\..+"`, XSD-Patterns sind implizit verankert).
 * Ein freier Name wie "Max Mustermann" macht die gesamte IDS-Datei schema-ungültig.
 */
const IDS_AUTHOR_PATTERN = /^[^@]+@[^.]+\..+$/;

/** Returns true if the given value satisfies the IDS author pattern (an e-mail address). */
export function isValidIdsAuthor(value: string | null | undefined): boolean {
  if (!value) return false;
  return IDS_AUTHOR_PATTERN.test(value.trim());
}

/** Returns true if the given IDS rule is fully valid (all required fields filled). */
export function isIdsRuleValid(rule: IDSRule): boolean {
  return getIdsValidationIssues(rule).length === 0;
}

/** Returns human-readable messages for all incomplete required IDS fields. */
export interface IDSValidationIssue {
  message: string;
  specificationIndex?: number;
  section?: 'metadata' | 'applicability' | 'requirements';
  facetId?: string;
}

/** Returns structured validation issues so the IDS editor can direct users to the affected field. */
export function getIdsValidationIssues(rule: IDSRule): IDSValidationIssue[] {
  const issues: IDSValidationIssue[] = [];
  const specificationNameCounts = new Map<string, number>();

  rule.specifications.forEach(spec => {
    const name = spec.name?.trim().toLowerCase();
    if (name) specificationNameCounts.set(name, (specificationNameCounts.get(name) ?? 0) + 1);
  });

  if (!rule.info.title?.trim()) {
    issues.push({
      message: 'IDS-Metadaten: Titel ist ein Pflichtfeld.',
      section: 'metadata',
    });
  }

  if (rule.info.author?.trim() && !isValidIdsAuthor(rule.info.author)) {
    issues.push({
      message: 'IDS-Metadaten: Autor muss eine E-Mail-Adresse sein (z. B. name@firma.de).',
      section: 'metadata',
    });
  }

  if (rule.info.date?.trim() && !isValidIsoDate(rule.info.date)) {
    issues.push({
      message: 'IDS-Metadaten: Datum muss im Format YYYY-MM-DD angegeben werden.',
      section: 'metadata',
    });
  }

  if (rule.specifications.length === 0) {
    issues.push({message: 'Mindestens eine Spezifikation ist erforderlich.'});
  }

  rule.specifications.forEach((spec, specIndex) => {
    const specificationLabel = spec.name?.trim() || `Spezifikation ${specIndex + 1}`;
    if (!spec.name?.trim()) {
      issues.push({
        message: `${specificationLabel}: Name ist ein Pflichtfeld.`,
        specificationIndex: specIndex,
        section: 'metadata',
      });
    } else if ((specificationNameCounts.get(spec.name.trim().toLowerCase()) ?? 0) > 1) {
      issues.push({
        message: `${specificationLabel}: Name muss eindeutig sein.`,
        specificationIndex: specIndex,
        section: 'metadata',
      });
    }

    appendFacetValidationIssues(issues, specificationLabel, specIndex, 'Anwendbarkeit', 'applicability', spec.applicability);
    appendFacetValidationIssues(issues, specificationLabel, specIndex, 'Anforderungen', 'requirements', spec.requirements);
  });

  return issues;
}

/** Returns true if a facet has missing required fields. */
export function isFacetInvalid(facet: IDSFacet): boolean {
  switch (facet.type) {
    case 'entity':
    case 'entityGroup':
      return isValueFieldEmpty(facet.entityName);
    case 'attribute':
      return isValueFieldEmpty(facet.attributeName);
    case 'property':
      return isValueFieldEmpty(facet.propertyName);
    case 'classification':
      return isValueFieldEmpty(facet.classificationSystem);
    case 'material':
      return isValueFieldEmpty(facet.materialName);
    case 'partOf':
      return !facet.relation?.trim() || isValueFieldEmpty(facet.partOfEntityName);
    default:
      return false;
  }
}

function appendFacetValidationIssues(
  issues: IDSValidationIssue[],
  specificationLabel: string,
  specificationIndex: number,
  sectionLabel: string,
  section: 'applicability' | 'requirements',
  facets: IDSFacet[],
): void {
  facets.forEach((facet, facetIndex) => {
    if (!isFacetInvalid(facet)) return;
    issues.push({
      message: `${specificationLabel}: ${sectionLabel} – ${FACET_TYPE_LABELS[facet.type]} ${facetIndex + 1}: ${getFacetValidationMessage(facet)}`,
      specificationIndex,
      section,
      facetId: facet.id,
    });
  });
}

function getFacetValidationMessage(facet: IDSFacet): string {
  switch (facet.type) {
    case 'entity':
      return 'Entitätsname ist ein Pflichtfeld.';
    case 'entityGroup':
      return 'Entitätengruppe ist ein Pflichtfeld.';
    case 'attribute':
      return 'Attributname ist ein Pflichtfeld.';
    case 'property':
      return 'Eigenschaftsname ist ein Pflichtfeld.';
    case 'classification':
      return 'Klassifikationssystem ist ein Pflichtfeld.';
    case 'material':
      return 'Materialname ist ein Pflichtfeld.';
    case 'partOf':
      return 'Beziehung und Entitätsname sind Pflichtfelder.';
  }
}

/** Returns a human-readable display string for any IDSValueField, or the given fallback.
 *  @param maxEnumItems  Max number of enum values to show before truncating with "+n" (default 1). */
export function getValueFieldDisplay(field: IDSValueField, fallback = '...', maxEnumItems = 1): string {
  switch (field.dataType) {
    case 'Wert':
      return field.simpleValue || fallback;
    case 'Aufzählung':
      if (!field.enumValues?.length) return fallback;
      if (field.enumValues.length <= maxEnumItems) return field.enumValues.join(', ');
      const shown = field.enumValues.slice(0, maxEnumItems).join(', ');
      const remaining = field.enumValues.length - maxEnumItems;
      return `${shown} +${remaining}`;
    case 'Muster':
      return field.pattern ? `Muster: ${field.pattern}` : fallback;
    case 'Grenzen':
      if (field.rangeMin && field.rangeMax) return `${field.rangeMin} – ${field.rangeMax}`;
      if (field.rangeMin) return `≥ ${field.rangeMin}`;
      if (field.rangeMax) return `≤ ${field.rangeMax}`;
      return fallback;
    case 'Länge':
      return field.lengthValue ? `Länge: ${field.lengthValue}` : fallback;
    case 'Länge Grenzen':
      if (field.lengthMin && field.lengthMax) return `Länge: ${field.lengthMin} – ${field.lengthMax}`;
      if (field.lengthMin) return `Länge ≥ ${field.lengthMin}`;
      if (field.lengthMax) return `Länge ≤ ${field.lengthMax}`;
      return fallback;
    default:
      return field.simpleValue || fallback;
  }
}

/** Textfragment für eine Anforderungs-Facette, z.B. "die Eigenschaft Wandbekleidung". */
export function getRequirementFacetFragment(facet: IDSFacet): string {
  switch (facet.type) {
    case 'entity': return 'die Entität ' + getValueFieldDisplay(facet.entityName);
    case 'entityGroup': return 'die Entitätengruppe ' + getValueFieldDisplay(facet.entityName);
    case 'attribute': return 'das Attribut ' + getValueFieldDisplay(facet.attributeName);
    case 'property': return 'die Eigenschaft ' + getValueFieldDisplay(facet.propertyName);
    case 'classification': return 'die Klassifikation ' + getValueFieldDisplay(facet.classificationSystem);
    case 'material': return 'das IfcMaterial ' + getValueFieldDisplay(facet.materialName);
    case 'partOf': return 'Teil von ' + getValueFieldDisplay(facet.partOfEntityName);
    default: return '...';
  }
}

/** Verb-Konstruktion je Anwendbarkeit (Kardinalität) einer Anforderung. */
const REQUIREMENT_NECESSITY_VERB: Record<NecessityType, string> = {
  required: 'müssen',
  optional: 'dürfen',
  prohibited: 'dürfen nicht',
};

/**
 * Baut den natürlichsprachlichen Hinweistext für die Anforderungen einer Spezifikation auf,
 * gruppiert nach der Anwendbarkeit (Erforderlich/Optional/Verboten) jeder einzelnen Anforderung -
 * z.B. "Die Elemente müssen die Eigenschaft U-Wert enthalten und dürfen die Eigenschaft Name
 * enthalten und dürfen nicht die Eigenschaft Betonklasse enthalten."
 * Fehlt bei einer (älteren) Anforderung die Kardinalität, wird sie wie "required" behandelt
 * (bisheriges Standardverhalten).
 */
export function buildRequirementsSummary(facets: IDSFacet[]): string {
  if (!facets.length) return '';

  const groups: Record<NecessityType, IDSFacet[]> = {required: [], optional: [], prohibited: []};
  for (const facet of facets) {
    groups[facet.metaNecessity ?? 'required'].push(facet);
  }

  const clauses = (['required', 'optional', 'prohibited'] as NecessityType[])
    .filter(necessity => groups[necessity].length > 0)
    .map(necessity => {
      const fragments = groups[necessity].map(f => getRequirementFacetFragment(f)).join(' und ');
      return `${REQUIREMENT_NECESSITY_VERB[necessity]} ${fragments} enthalten`;
    });

  return `Die Elemente ${clauses.join(' und ')}.`;
}
